import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/context/ThemeContext";

export function DottedSurface({ className, style, ...props }) {
  const containerRef = useRef(null);
  const animationRef = useRef(0);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let didCancel = false;
    let cleanup = () => {};

    const init = async () => {
      const THREE = await import("three");
      if (didCancel || !containerRef.current) return;

      const AMOUNT_X = 40;
      const AMOUNT_Y = 60;
      const SEPARATION = 115;

      const getSize = () => {
        const rect = container.getBoundingClientRect();
        return {
          width: Math.max(rect.width, 1),
          height: Math.max(rect.height, 1),
        };
      };

      const { width, height } = getSize();

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(54, width / height, 1, 9000);
      camera.position.set(0, 260, 780);
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(width, height);
      renderer.setClearAlpha(0);

      container.appendChild(renderer.domElement);

      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const colors = [];

      const pointColor = new THREE.Color(
        isDark ? "#B0E0F2" : "#0033A0"
      );

      for (let ix = 0; ix < AMOUNT_X; ix += 1) {
        for (let iy = 0; iy < AMOUNT_Y; iy += 1) {
          const x = ix * SEPARATION - (AMOUNT_X * SEPARATION) / 2;
          const y = 0;
          const z = iy * SEPARATION - (AMOUNT_Y * SEPARATION) / 2;

          positions.push(x, y, z);
          colors.push(pointColor.r, pointColor.g, pointColor.b);
        }
      }

      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3)
      );
      geometry.setAttribute(
        "color",
        new THREE.Float32BufferAttribute(colors, 3)
      );

      const material = new THREE.PointsMaterial({
        size: isDark ? 7.2 : 8.5,
        vertexColors: true,
        transparent: true,
        opacity: isDark ? 0.8 : 1.0,
        sizeAttenuation: true,
        depthWrite: false,
      });

      const points = new THREE.Points(geometry, material);
      scene.add(points);

      let count = 0;

      const animate = () => {
        const positionAttribute = geometry.attributes.position;
        const positionArray = positionAttribute.array;

        let i = 0;
        for (let ix = 0; ix < AMOUNT_X; ix += 1) {
          for (let iy = 0; iy < AMOUNT_Y; iy += 1) {
            const index = i * 3;
            positionArray[index + 1] =
              Math.sin((ix + count) * 0.20) * 52 +
              Math.sin((iy + count) * 0.32) * 54;
            i += 1;
          }
        }

        positionAttribute.needsUpdate = true;
        renderer.render(scene, camera);
        count += 0.072;

        if (!prefersReducedMotion) {
          animationRef.current = requestAnimationFrame(animate);
        }
      };

      const handleResize = () => {
        const next = getSize();
        camera.aspect = next.width / next.height;
        camera.updateProjectionMatrix();
        renderer.setSize(next.width, next.height);
      };

      window.addEventListener("resize", handleResize);
      animate();

      cleanup = () => {
        window.removeEventListener("resize", handleResize);
        cancelAnimationFrame(animationRef.current);

        scene.remove(points);
        geometry.dispose();
        material.dispose();
        renderer.dispose();

        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };
    };

    init();

    return () => {
      didCancel = true;
      cleanup();
    };
  }, [isDark]);

  return (
    <div
      ref={containerRef}
      className={cn("pointer-events-none absolute inset-0", className)}
      style={{
        mixBlendMode: isDark ? "screen" : "multiply",
        ...style,
      }}
      {...props}
    />
  );
}
